import React, { HTMLAttributes, PropsWithChildren, useRef } from 'react';
import { style } from 'typestyle';
import { ADALONG_COLORS } from '@src/styles';
import { useWindowSize } from '@src/hooks/useWindowSize';

export const DEFAULT_NUMBER_PER_ROW = 4;

interface Props extends PropsWithChildren<HTMLAttributes<HTMLDivElement>> {
  columnsPerRow: number;
  minMediaWidth?: number;
}

/** The responsive masonry item frame shared by complete cards and thumbnails. */
export function MediaContainer({ children, columnsPerRow, minMediaWidth, ...props }: Props) {
  // lets rerender when the window size change
  useWindowSize();

  const ref = useRef<HTMLDivElement>(null);
  const width = bestWidthPercent(ref.current?.parentElement?.offsetWidth, columnsPerRow, minMediaWidth);

  return (
    <div className={mediaelementClass} ref={ref} style={{ width }} {...props}>
      {children}
    </div>
  );
}

export function bestWidthPercent(
  parentWidth?: number,
  columnsPerRow: number = DEFAULT_NUMBER_PER_ROW,
  minMediaWidth: number = 240,
): string {
  let r = 100 / columnsPerRow;
  if (parentWidth) {
    if (parentWidth < minMediaWidth * 2) {
      r = 100;
    } else if (parentWidth < minMediaWidth * 3) {
      r = 100 / 2;
    } else if (parentWidth < minMediaWidth * 4) {
      r = 100 / 3;
    }
  }
  return `${r}%`;
}

export const mediaContainerClass = style({
  userSelect: 'none',
  '-moz-user-select': 'none',
  '-webkit-user-select': 'none',
  '-ms-user-select': 'none',
  position: 'relative',
  height: '100%',
  background: '#77777712',
  borderRadius: 12,
  overflow: 'hidden',
  border: `2px solid ${ADALONG_COLORS.SMOOTHER_BLACK}`,
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
  $nest: {
    '&[data-selected="true"]': {
      outline: '2px solid #4E70C8',
      filter: 'brightness(80%)',
    },
  },
});

export const mediaelementClass = style({
  position: 'relative',
  padding: '0 6px 12px',
  transition: 'opacity 0.13s, filter 0.13s',
  borderRadius: 12,
  $nest: {
    '&[data-blur="true"]': {
      opacity: 0.6,
      filter: 'blur(1px)',
    },
    '&[data-failed]': {
      $nest: {
        '&[data-hideonfailed="true"]': {
          padding: 0,
          height: 0,
          overflow: 'hidden',
        },
      },
      height: 150,
      cursor: 'default',
    },
  },
});
